/**
 * career-evidence-ledger — the durable, all-claims Belegregister (route
 * `/compass/evidence`). Grows from the extraction review's claim cards (REUSED via
 * `renderClaimCard`); the review stays focused on ingest-run proposals, this is the
 * durable graded ledger. Filter by grade / status / origin (registered `is-*`
 * vocabulary); confirm / edit / discard as `data-act` verbs matching the api verbs.
 *
 * Push-fed: the Evidence stream is subscribed on attach; replay hydrates the shared
 * evidence store and live claim.* ticks reconcile through the ONE apply path.
 * Reorder-on-confirm (N5): the `alle` projection is id-sorted, so a confirmed claim
 * keeps its position — status changes as an `is-*` class, never a scroll-jarring move.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { EvidenceStoreToken, I18nToken, PushToken, SessionToken, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { careerStream, type EvidenceClaimView, type EvidenceGrade, type EvidenceStore, type ShellSession } from "../domain"
import { renderClaimCard } from "../lib/claim-card"

type GradeFilter = "all" | EvidenceGrade
type StatusFilter = "all" | "proposed" | "confirmed"

const GRADE_FILTERS: GradeFilter[] = ["all", "evidenced", "self-positioning", "gap"]
const STATUS_FILTERS: StatusFilter[] = ["all", "proposed", "confirmed"]
const GRADE_FILTER_KEY: Record<GradeFilter, CareerKey> = {
  all: "ledger.filter.all",
  evidenced: "review.grade.evidenced",
  "self-positioning": "review.grade.selfPositioning",
  gap: "review.grade.gap",
}
const STATUS_FILTER_KEY: Record<StatusFilter, CareerKey> = {
  all: "ledger.filter.all",
  proposed: "ledger.status.proposed",
  confirmed: "ledger.status.confirmed",
}

export class LedgerVm implements ViewModel {
  readonly claims: ReadonlySignal<readonly EvidenceClaimView[]>
  readonly gradeFilter: Signal<GradeFilter> = signal<GradeFilter>("all")
  readonly statusFilter: Signal<StatusFilter> = signal<StatusFilter>("all")
  readonly editingId: Signal<string | null> = signal<string | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(
    private readonly store: EvidenceStore,
    private readonly push: PushChannel,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.claims = store.list("alle")
    this.renderDeps = [this.claims, this.gradeFilter, this.statusFilter, this.editingId, this.error, store.pending]
  }

  attach(): () => void {
    const userId = this.session.user?.sub
    if (userId) this.push.subscribe([careerStream(userId, "Evidence")])
    return () => {}
  }

  filtered(): EvidenceClaimView[] {
    const g = this.gradeFilter.value
    const s = this.statusFilter.value
    return this.claims.value.filter((c) => (g === "all" || c.grade === g) && (s === "all" || c.status === s))
  }

  counts(): { evidenced: number; selfPositioning: number; gap: number; confirmed: number } {
    const all = this.claims.value
    return {
      evidenced: all.filter((c) => c.grade === "evidenced").length,
      selfPositioning: all.filter((c) => c.grade === "self-positioning").length,
      gap: all.filter((c) => c.grade === "gap").length,
      confirmed: all.filter((c) => c.status === "confirmed").length,
    }
  }

  confirmClaim(claimId: string): void {
    void this.store.command({ type: "confirm-claim", payload: { claimId } })
  }
  discardClaim(claimId: string): void {
    void this.store.command({ type: "discard-claim", payload: { claimId } })
  }
  toggleEdit(claimId: string): void {
    this.editingId.set(this.editingId.value === claimId ? null : claimId)
  }
  async saveEdit(claimId: string, statement: string): Promise<void> {
    const claim = this.store.entity(claimId as never).value
    if (!claim || statement.trim().length === 0) return
    this.editingId.set(null)
    try {
      await apiPost("/api/evidence/edit", {
        claimId,
        claim: { statement, grade: claim.grade, origin: claim.origin, sources: claim.sources, tags: claim.tags },
      })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }
}

export class CareerEvidenceLedger extends KitElement<LedgerVm> {
  protected createViewModel(ctx: KitContext): LedgerVm {
    return new LedgerVm(ctx.inject(EvidenceStoreToken), ctx.inject(PushToken), ctx.inject(SessionToken), ctx.inject(I18nToken))
  }

  private filterBar(vm: LedgerVm): TemplateResult {
    return html`
      <div class="ledger-filters" role="group" aria-label=${vm.t("ledger.filters")}>
        <div class="ledger-filter-group">
          ${GRADE_FILTERS.map(
            (g) => html`<button
              data-act="set-grade-filter"
              data-grade=${g}
              class=${classMap({ "filter-chip": true, "is-active": vm.gradeFilter.value === g })}
              aria-pressed=${vm.gradeFilter.value === g ? "true" : "false"}
              lang="de"
            >${vm.t(GRADE_FILTER_KEY[g])}</button>`,
          )}
        </div>
        <div class="ledger-filter-group">
          ${STATUS_FILTERS.map(
            (s) => html`<button
              data-act="set-status-filter"
              data-status=${s}
              class=${classMap({ "filter-chip": true, "is-active": vm.statusFilter.value === s })}
              aria-pressed=${vm.statusFilter.value === s ? "true" : "false"}
              lang="de"
            >${vm.t(STATUS_FILTER_KEY[s])}</button>`,
          )}
        </div>
      </div>
    `
  }

  protected render(vm: LedgerVm): TemplateResult {
    const filtered = vm.filtered()
    const c = vm.counts()
    const err = vm.error.value
    return html`
      <section class="surface ledger" aria-labelledby="ledger-title">
        <h1 id="ledger-title" class="surface-title" lang="de">${vm.t("surface.belege.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("ledger.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        <p class="ledger-counts">
          <span class="chip is-evidenced" lang="de">${vm.t("ledger.count.evidenced", { n: c.evidenced })}</span>
          <span class="chip is-self-positioning" lang="de">${vm.t("ledger.count.selfPositioning", { n: c.selfPositioning })}</span>
          <span class="chip is-gap" lang="de">${vm.t("ledger.count.gap", { n: c.gap })}</span>
          <span class="chip is-confirmed" lang="de">${vm.t("ledger.count.confirmed", { n: c.confirmed })}</span>
        </p>

        ${this.filterBar(vm)}

        ${vm.claims.value.length === 0
          ? html`<p class="surface-empty" lang="de">${vm.t("ledger.empty")}</p>`
          : filtered.length === 0
            ? html`<p class="surface-empty" lang="de">${vm.t("ledger.emptyFiltered")}</p>`
            : html`<ol class="claim-list" role="list">
                ${repeat(filtered, (claim) => claim.urn, (claim) => renderClaimCard(claim, { editing: vm.editingId.value === claim.id, t: vm.t }))}
              </ol>`}
      </section>
    `
  }

  protected override actions(vm: LedgerVm): ActionMap {
    return {
      "set-grade-filter": ({ grade }) => { if (grade) vm.gradeFilter.set(grade as GradeFilter) },
      "set-status-filter": ({ status }) => { if (status) vm.statusFilter.set(status as StatusFilter) },
      "confirm-claim": ({ claimId }) => { if (claimId) vm.confirmClaim(claimId) },
      "discard-claim": ({ claimId }) => { if (claimId) vm.discardClaim(claimId) },
      "edit-toggle": ({ claimId }) => { if (claimId) vm.toggleEdit(claimId) },
      "edit-claim": ({ claimId }, event) => {
        if (!claimId) return
        const card = (event.target as HTMLElement).closest(".claim-card")
        const input = card?.querySelector<HTMLInputElement>("input.claim-edit-input")
        if (input) void vm.saveEdit(claimId, input.value)
      },
    }
  }
}
